/**
 * Security Dashboard - Version 0.1
 * Initial implementation with core security monitoring features
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Col, 
  Row, 
  Card, 
  Badge, 
  Table, 
  Button, 
  Form, 
  Tab, 
  Tabs 
} from "react-bootstrap";
import DatePicker from 'components/base/DatePicker';
import ShellCommand from './ShellCommand';
import { 
  Activity, 
  AlertTriangle, 
  ChevronRight, 
  Lock, 
  Clock, 
  DownloadCloud, 
  Eye, 
  Globe, 
  HardDrive, 
  LucideProps, 
  RefreshCw, 
  Search, 
  Server, 
  Settings, 
  Shield, 
  Terminal, 
  User, 
  Wifi 
} from 'lucide-react';

// Wrapper para iconos
const IconWrapper: React.FC<{
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
  className?: string;
  size?: number;
  color?: string;
}> = ({ 
  icon: Icon, 
  className = "", 
  size = 16,
  color 
}) => {
  return (
    <div 
      className={`d-inline-flex align-items-center justify-content-center ${className}`} 
      style={{ width: size, height: size }}
    >
      <Icon size={size} color={color} />
    </div>
  );
};

// Interfaces
interface SecurityTool {
  id: string;
  name: string;
  category: 'scanner' | 'forensic' | 'network' | 'crypto' | 'osint' | 'malware' | 'other';
  status: 'running' | 'stopped' | 'error';
  lastRun?: Date;
  description: string;
  command: string;
}

interface SystemHost {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline' | 'warning';
  os: string;
  services: string[];
  vulnerabilities: number;
  lastScan?: Date;
}

interface SecurityAlert {
  id: string;
  type: 'intrusion' | 'vulnerability' | 'malware' | 'anomaly' | 'scan' | 'access';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  source: string;
  target?: string;
  timestamp: Date;
  message: string;
  resolved: boolean;
}

interface ScanResult {
  id: string;
  toolId: string;
  hostId: string;
  timestamp: Date;
  findings: number;
  details: string;
  status: 'completed' | 'failed' | 'in_progress';
}

const SecurityDashboard: React.FC = () => {
  // State declarations
  const [securityTools, setSecurityTools] = useState<SecurityTool[]>([]);
  const [system, setSystem] = useState<SystemHost[]>([]);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [activeTool, setActiveTool] = useState<string | undefined>();
  const [activeSystem, setActiveSystem] = useState<string | undefined>();
  const [showTerminal, setShowTerminal] = useState(false);
  const [activeTab, setActiveTab] = useState<'tools' | 'systems' | 'scan'>('tools');

  // Utility function for generating random alerts
  const generateRandomAlerts = useCallback((count: number): SecurityAlert[] => {
    const types: SecurityAlert['type'][] = ['intrusion', 'vulnerability', 'malware', 'anomaly', 'scan', 'access'];
    const severities: SecurityAlert['severity'][] = ['critical', 'high', 'medium', 'low', 'info'];
    const sources = ['192.168.1.10', '192.168.1.20', '192.168.1.30', '192.168.1.50', '10.0.0.15', '8.8.8.8', '1.1.1.1'];
    const targets = ['192.168.1.10', '192.168.1.20', '192.168.1.30', '192.168.1.1', '192.168.1.50'];
    
    const messages = {
      intrusion: [
        'Intento de acceso no autorizado detectado',
        'Múltiples intentos fallidos de autenticación SSH',
        'Posible ataque de fuerza bruta en curso',
        'Intento de explotación de vulnerabilidad CVE-2023-1234'
      ],
      vulnerability: [
        'Versión obsoleta de OpenSSL detectada',
        'Servidor web expone información sensible',
        'Falta parche de seguridad crítico',
        'SSL/TLS configurado inseguramente'
      ],
      malware: [
        'Actividad sospechosa de malware detectada',
        'Archivo infectado encontrado en /var/www',
        'Comunicación con servidor C&C conocido',
        'Script malicioso detectado en página web'
      ],
      anomaly: [
        'Tráfico de red inusual detectado',
        'Pico en el uso de CPU sin explicación',
        'Actividad de usuario fuera de horario normal',
        'Transferencia de datos anómala'
      ],
      scan: [
        'Escaneo de puertos detectado',
        'Intento de enumeración de directorios',
        'Escaneo de vulnerabilidades desde IP externa',
        'Fingerprinting de servicios detectado'
      ],
      access: [
        'Acceso a zona restringida',
        'Elevación de privilegios detectada',
        'Usuario accedió a recursos sensibles',
        'Modificación de archivos críticos del sistema'
      ]
    };
    
    return Array(count).fill(0).map((_, index) => {
      const type = types[Math.floor(Math.random() * types.length)];
      const severity = severities[Math.floor(Math.random() * severities.length)];
      const source = sources[Math.floor(Math.random() * sources.length)];
      const target = targets[Math.floor(Math.random() * targets.length)];
      const timestamp = new Date(Date.now() - Math.floor(Math.random() * 86400000));
      const messageList = messages[type];
      const message = messageList[Math.floor(Math.random() * messageList.length)];
      
      return {
        id: `alert_${Date.now()}_${index}`,
        type,
        severity,
        source,
        target,
        timestamp,
        message,
        resolved: Math.random() > 0.7,
      };
    });
  }, []);

  // Utility function for getting stats
  const getStats = useCallback(() => {
    return {
      totalAlerts: alerts.length,
      criticalAlerts: alerts.filter(a => a.severity === 'critical' && !a.resolved).length,
      vulnerabilities: system.reduce((acc, s) => acc + s.vulnerabilities, 0),
      systemsOnline: system.filter(s => s.status === 'online').length,
      toolsRunning: securityTools.filter(t => t.status === 'running').length,
      scans: scanResults.length
    };
  }, [alerts, system, securityTools, scanResults]);

  // Main useEffect for initialization
  useEffect(() => {
    // Initial data setup
    const initialTools: SecurityTool[] = [
      {
        id: 'tool_1',
        name: 'Nmap Scanner',
        category: 'scanner',
        status: 'stopped',
        description: 'Escaneo de puertos y servicios',
        command: 'nmap -sV -p 1-1000'
      },
      {
        id: 'tool_2',
        name: 'Metasploit Framework',
        category: 'scanner',
        status: 'stopped',
        description: 'Framework de pruebas de penetración',
        command: 'msfconsole'
      },
      {
        id: 'tool_3',
        name: 'Wireshark Capture',
        category: 'network',
        status: 'running',
        lastRun: new Date(Date.now() - 3600000),
        description: 'Captura y análisis de tráfico de red',
        command: 'tshark -i eth0 -w /tmp/capture.pcap'
      }
    ];

    const initialSystems: SystemHost[] = [
      {
        id: 'host_1',
        name: 'Servidor Web Principal',
        ip: '192.168.1.10',
        status: 'online',
        os: 'Ubuntu 22.04 LTS',
        services: ['nginx', 'mysql', 'php-fpm', 'ssh'],
        vulnerabilities: 3,
        lastScan: new Date(Date.now() - 43200000)
      },
      {
        id: 'host_2',
        name: 'Servidor de Base de Datos',
        ip: '192.168.1.20',
        status: 'online',
        os: 'CentOS 7',
        services: ['mariadb', 'ssh'],
        vulnerabilities: 1,
        lastScan: new Date(Date.now() - 86400000)
      }
    ];

    // Set initial state
    setSecurityTools(initialTools);
    setSystem(initialSystems);
    setAlerts([]);
    setScanResults([]);

    // Real-time simulation interval
    const interval = setInterval(() => {
      // Añadir una nueva alerta aleatoria cada minuto
      if (Math.random() > 0.7) {
        setAlerts(prev => [generateRandomAlerts(1)[0], ...prev].slice(0, 30));
      }
      
      // Actualizar estado de herramientas aleatoriamente
      if (Math.random() > 0.9) {
        setSecurityTools(prev => {
          const newTools = [...prev];
          const randomIndex = Math.floor(Math.random() * newTools.length);
          newTools[randomIndex] = {
            ...newTools[randomIndex],
            status: newTools[randomIndex].status === 'running' ? 'stopped' : 'running',
            lastRun: newTools[randomIndex].status === 'stopped' ? new Date() : newTools[randomIndex].lastRun
          };
          return newTools;
        });
      }
    }, 60000);

    // Cleanup
    return () => clearInterval(interval);
  }, [generateRandomAlerts]);

  // Nested Components
  const ToolDetail: React.FC<{ toolId: string }> = ({ toolId }) => {
    const tool = securityTools.find(t => t.id === toolId);
    const selectedSystem = activeSystem ? system.find(s => s.id === activeSystem) : undefined;
    
    if (!tool) return null;

    const toolResults = scanResults.filter(r => r.toolId === toolId);

    return (
      <Card className="h-100">
        <Card.Header className="p-3 bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{tool.name}</h5>
          <Badge bg={
            tool.status === 'running' ? 'success' :
            tool.status === 'error' ? 'danger' : 'secondary'
          }>
            {tool.status === 'running' ? 'En ejecución' :
             tool.status === 'error' ? 'Error' : 'Detenido'}
          </Badge>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-2">
                <strong>Categoría:</strong> {tool.category}
              </div>
              <div className="mb-2">
                <strong>Descripción:</strong> {tool.description}
              </div>
              <div className="mb-2">
                <strong>Último inicio:</strong> {tool.lastRun ? tool.lastRun.toLocaleString() : 'Nunca'}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <strong>Comando:</strong> 
                <code className="d-block bg-light p-2 rounded">{tool.command}</code>
              </div>
            </Col>
          </Row>

          <div className="d-flex gap-2 mb-4">
            <Button 
              variant="primary" 
              onClick={() => setShowTerminal(true)}
              className="d-flex align-items-center"
            >
              <IconWrapper icon={Terminal} className="me-2" />
              Ejecutar Herramienta
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={() => {/* Posible acción adicional */}}
              className="d-flex align-items-center"
            >
              <IconWrapper icon={Settings} className="me-2" />
              Configurar
            </Button>
          </div>

          <h6 className="fw-bold mb-3">Historial de Escaneos</h6>
          {toolResults.length > 0 ? (
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Sistema</th>
                  <th>Fecha</th>
                  <th>Hallazgos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {toolResults.map((result) => {
                  const targetSystem = system.find(s => s.id === result.hostId);
                  return (
                    <tr key={result.id}>
                      <td>{targetSystem?.name || 'Sistema Desconocido'}</td>
                      <td>{result.timestamp.toLocaleString()}</td>
                      <td>
                        <Badge bg={
                          result.findings > 5 ? 'danger' : 
                          result.findings > 0 ? 'warning' : 'success'
                        }>
                          {result.findings}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={
                          result.status === 'completed' ? 'success' :
                          result.status === 'failed' ? 'danger' : 'info'
                        }>
                          {result.status === 'completed' ? 'Completado' :
                          result.status === 'failed' ? 'Fallido' : 'En progreso'}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0 text-primary me-2"
                          onClick={() => setShowTerminal(true)}
                          >
                            <IconWrapper icon={Eye} />
                          </Button>
                          <Button variant="link" size="sm" className="p-0 text-success">
                            <IconWrapper icon={DownloadCloud} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-center text-muted p-3">
                No hay escaneos registrados para esta herramienta
              </div>
            )}
          </Card.Body>
        </Card>
      );
    };
  
    const SystemDetail: React.FC<{ systemId: string }> = ({ systemId }) => {
      const selectedSystem = system.find(s => s.id === systemId);
      
      if (!selectedSystem) return null;
  
      const systemScans = scanResults.filter(r => r.hostId === systemId);
  
      return (
        <Card className="h-100">
          <Card.Header className="p-3 bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{selectedSystem.name}</h5>
            <Badge bg={
              selectedSystem.status === 'online' ? 'success' :
              selectedSystem.status === 'warning' ? 'warning' : 'danger'
            }>
              {selectedSystem.status === 'online' ? 'Online' :
               selectedSystem.status === 'warning' ? 'Advertencia' : 'Offline'}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Row className="mb-4">
              <Col md={6}>
                <div className="mb-2">
                  <strong>IP:</strong> {selectedSystem.ip}
                </div>
                <div className="mb-2">
                  <strong>Sistema Operativo:</strong> {selectedSystem.os}
                </div>
                <div className="mb-2">
                  <strong>Último escaneo:</strong> {selectedSystem.lastScan ? selectedSystem.lastScan.toLocaleString() : 'Nunca'}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-2">
                  <strong>Vulnerabilidades:</strong>{" "}
                  <Badge bg={
                    selectedSystem.vulnerabilities > 5 ? 'danger' :
                    selectedSystem.vulnerabilities > 0 ? 'warning' : 'success'
                  }>
                    {selectedSystem.vulnerabilities}
                  </Badge>
                </div>
                <div className="mb-2">
                  <strong>Servicios:</strong> {selectedSystem.services.join(', ')}
                </div>
              </Col>
            </Row>
  
            <div className="d-flex gap-2 mb-4">
              <Button 
                variant="primary" 
                onClick={() => setShowTerminal(true)}
                className="d-flex align-items-center"
              >
                <IconWrapper icon={Search} className="me-2" />
                Escanear Sistema
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowTerminal(true)}
                className="d-flex align-items-center"
              >
                <IconWrapper icon={Terminal} className="me-2" />
                Conectar
              </Button>
            </div>
  
            <h6 className="fw-bold mb-3">Historial de Escaneos</h6>
            {systemScans.length > 0 ? (
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Herramienta</th>
                    <th>Fecha</th>
                    <th>Hallazgos</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {systemScans.map((result) => {
                    const tool = securityTools.find(t => t.id === result.toolId);
                    return (
                      <tr key={result.id}>
                        <td>{tool?.name || 'Herramienta Desconocida'}</td>
                        <td>{result.timestamp.toLocaleString()}</td>
                        <td>
                          <Badge bg={
                            result.findings > 5 ? 'danger' : 
                            result.findings > 0 ? 'warning' : 'success'
                          }>
                            {result.findings}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={
                            result.status === 'completed' ? 'success' :
                            result.status === 'failed' ? 'danger' : 'info'
                          }>
                            {result.status === 'completed' ? 'Completado' :
                            result.status === 'failed' ? 'Fallido' : 'En progreso'}
                          </Badge>
                        </td>
                        <td>
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="p-0 text-primary me-2"
                            onClick={() => setShowTerminal(true)}
                          >
                            <IconWrapper icon={Eye} />
                          </Button>
                          <Button variant="link" size="sm" className="p-0 text-success">
                            <IconWrapper icon={DownloadCloud} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-center text-muted p-3">
                No hay escaneos registrados para este sistema
              </div>
            )}
          </Card.Body>
        </Card>
      );
    };
  
    // Main Panel Component
    const MainPanel: React.FC = () => {
      if (showTerminal) {
        return <ShellCommand />;
      }
    
      if (activeTool) {
        return <ToolDetail toolId={activeTool} />;
      }
    
      if (activeSystem) {
        return <SystemDetail systemId={activeSystem} />;
      }
    
      // Default Dashboard
      return (
        <Card className="h-100 bg-body-emphasis">
          <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
            <h5 className="mb-0 text-emphasis">Centro de Control de Seguridad</h5>
            <Button 
              variant="outline-light" 
              size="sm"
              onClick={() => setShowTerminal(true)}
            >
              <IconWrapper icon={Terminal} className="me-1" /> Abrir Terminal
            </Button>
          </Card.Header>
          <Card.Body className="text-center d-flex flex-column justify-content-center align-items-center">
            <IconWrapper icon={Shield} size={64} className="text-primary mb-3" />
            <h4 className="mb-3 text-emphasis">Panel de Control de Ciberseguridad</h4>
            <p className="text-muted">
              Selecciona una herramienta o sistema para ver detalles y ejecutar acciones.
            </p>
            
            <Row className="mt-4 w-100">
              <Col md={6} className="mb-3">
                <Card className="text-start border-secondary h-100 bg-emphasis">
                  <Card.Body>
                    <h5 className="card-title text-emphasis">
                      <IconWrapper icon={Settings} className="text-primary me-2" /> Herramientas
                    </h5>
                    <p className="text-muted">Accede a herramientas de seguridad como Nmap, Metasploit, Wireshark, etc.</p>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => setActiveTab('tools')}
                    >
                      Ver herramientas
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="text-start border-secondary h-100 bg-emphasis">
                  <Card.Body>
                    <h5 className="card-title text-emphasis">
                      <IconWrapper icon={Server} className="text-success me-2" /> Sistemas
                    </h5>
                    <p className="text-muted">Monitorea el estado de tus servidores y dispositivos de red.</p>
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      onClick={() => setActiveTab('systems')}
                    >
                      Ver sistemas
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      );
    };
  
    // Render stats
    const stats = getStats();
  
    // Dashboard statistics
    const securityStats = [
      {
        title: 'Alertas Activas',
        value: alerts.filter(a => !a.resolved).length,
        icon: <IconWrapper icon={AlertTriangle} size={24} />,
        variant: 'danger',
        trend: 12
      },
      {
        title: 'Vulnerabilidades',
        value: stats.vulnerabilities,
        icon: <IconWrapper icon={Shield} size={24} />,
        variant: 'warning',
        trend: -3
      },
      {
        title: 'Sistemas Online',
        value: `${stats.systemsOnline}/${system.length}`,
        icon: <IconWrapper icon={Server} size={24} />,
        variant: 'success',
        trend: 0
      },
      {
        title: 'Herramientas Activas',
        value: `${stats.toolsRunning}/${securityTools.length}`,
        icon: <IconWrapper icon={Settings} size={24} />,
        variant: 'primary',
        trend: 5
      }
    ];
  
    // Componentes de lista de herramientas y sistemas
    const SecurityTools: React.FC<{
      tools: SecurityTool[];
      onSelect: (toolId: string) => void;
      activeToolId: string | undefined;
    }> = ({ tools, onSelect, activeToolId }) => {
      return (
        <Card className="h-100 bg-pramiary">
          <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
            <h5 className="mb-0 text-body-highlight ">Herramientas de Seguridad</h5>
            <Form.Control 
              type="text" 
              placeholder="Buscar herramienta..." 
              size="sm" 
              className="w-50 text-white border-secondary"
            />
          </Card.Header>
          <Card.Body className="p-0">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {tools.map(tool => (
                <div 
                  key={tool.id}
                  className={`p-3 border-bottom border-secondary d-flex align-items-center cursor-pointer ${tool.id === activeToolId ? 'bg-secondary' : ''}`}
                  onClick={() => onSelect(tool.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="me-3 position-relative">
                    <div className="rounded p-2 text-body-tertiary" style={{ width: 40, height: 40 }}>
                      {tool.category === 'scanner' && <IconWrapper icon={Search} className="text-primary" size={20} />}
                      {tool.category === 'forensic' && <IconWrapper icon={HardDrive} className="text-purple" size={20} />}
                      {tool.category === 'network' && <IconWrapper icon={Wifi} className="text-success" size={20} />}
                      {tool.category === 'malware' && <IconWrapper icon={Shield} className="text-danger" size={20} />}
                      {tool.category === 'osint' && <IconWrapper icon={Globe} className="text-warning" size={20} />}
                      {(tool.category === 'crypto' || tool.category === 'other') && (
                        <IconWrapper icon={Lock} className="text-secondary" size={20} />
                      )}
                    </div>
                    <div 
                      className={`position-absolute top-0 end-0 rounded-circle ${
                        tool.status === 'running' ? 'bg-success' : 
                        tool.status === 'error' ? 'bg-danger' : 'bg-secondary'
                      }`} 
                      style={{ width: 10, height: 10 }}
                    ></div>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold text-white">{tool.name}</h6>
                    <small className="text-muted">{tool.description}</small>
                  </div>
                  <ChevronRight size={16} className="text-muted" />
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      );
    };
  
    const MonitoredSystems: React.FC<{
      systems: SystemHost[];
      onSelect: (systemId: string) => void;
      activeSystemId: string | undefined;
    }> = ({ systems, onSelect, activeSystemId }) => {
      return (
        <Card className="h-100 bg-dark">
          <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
            <h5 className="mb-0 text-white">Sistemas Monitoreados</h5>
            <Form.Control 
              type="text" 
              placeholder="Buscar sistema..." 
              size="sm" 
              className="w-50 bg-dark text-white border-secondary"
            />
          </Card.Header>
          <Card.Body className="p-0">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {systems.map(system => (
                <div 
                  key={system.id}
                  className={`p-3 border-bottom border-secondary d-flex align-items-center ${system.id === activeSystemId ? 'bg-secondary' : ''}`}
                  onClick={() => onSelect(system.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="me-3 position-relative">
                    <div className="rounded p-2 bg-secondary" style={{ width: 40, height: 40 }}>
                      <IconWrapper icon={Server} className="text-info" size={20} />
                    </div>
                    <div 
                      className={`position-absolute top-0 end-0 rounded-circle ${
                        system.status === 'online' ? 'bg-success' : 
                        system.status === 'warning' ? 'bg-warning' : 'bg-danger'
                      }`} 
                      style={{ width: 10, height: 10 }}
                    ></div>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold text-white">{system.name}</h6>
                    <small className="text-muted">{system.ip} • {system.os}</small>
                  </div>
                  <div className="me-2 text-end">
                    <Badge bg={system.vulnerabilities > 0 ? 'danger' : 'success'}>
                      {system.vulnerabilities} vuln.
                    </Badge>
                    {system.lastScan && (
                      <div className="small text-muted">
                        Escaneado: {system.lastScan.toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-muted" />
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      );
    };

  const SecurityAlerts: React.FC<{
    alerts: SecurityAlert[];
  }> = ({ alerts }) => {
    const getAlertVariant = (severity: SecurityAlert['severity']): string => {
      switch (severity) {
        case 'critical': return 'danger';
        case 'high': return 'warning';
        case 'medium': return 'primary';
        case 'low': return 'info';
        default: return 'secondary';
      }
    };
  
    const getAlertIcon = (type: SecurityAlert['type']): React.ReactNode => {
      switch (type) {
        case 'intrusion': return <IconWrapper icon={Shield} size={18} />;
        case 'vulnerability': return <IconWrapper icon={Lock} size={18} />;
        case 'malware': return <IconWrapper icon={AlertTriangle} size={18} />;
        case 'anomaly': return <IconWrapper icon={Activity} size={18} />;
        case 'scan': return <IconWrapper icon={Search} size={18} />;
        case 'access': return <IconWrapper icon={User} size={18} />;
        default: return <IconWrapper icon={AlertTriangle} size={18} />;
      }
    };
  
    const getTimeAgo = (date: Date): string => {
      const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
      return `${Math.floor(seconds / 86400)}d`;
    };
  
    const activeAlerts = alerts.filter(alert => !alert.resolved);
  
    return (
      <Card className="h-100 bg-emphasis">
        <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
          <h5 className="mb-0 text-emphasis">Alertas de Seguridad</h5>
          <Button variant="outline-light" size="sm" className="p-1">
            <IconWrapper icon={RefreshCw} />
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {activeAlerts.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <IconWrapper icon={Shield} size={32} className="mb-2 text-secondary" />
              <p className="text-emphasis">No hay alertas activas en este momento</p>
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {activeAlerts.slice(0, 10).map(alert => (
                <div 
                  key={alert.id} 
                  className="p-3 border-bottom border-secondary d-flex align-items-start"
                >
                  <div className={`me-3 text-${getAlertVariant(alert.severity)}`}>
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between mb-1">
                      <h6 className="mb-0 text-white">{alert.message}</h6>
                      <Badge bg={getAlertVariant(alert.severity)} className="text-uppercase">
                        {alert.severity}
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between small text-muted">
                      <span>
                        {alert.source} 
                        {alert.target && ` → ${alert.target}`}
                      </span>
                      <span className="d-flex align-items-center">
                        <IconWrapper icon={Clock} size={12} className="me-1" /> 
                        {getTimeAgo(alert.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Render the main dashboard
  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col md={9} xs="auto">
          <h2 className="mb-2 text-body-emphasis">Panel de Ciberseguridad</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Monitoreo y gestión de seguridad
          </h5>
        </Col>
        <Col md={3} xs="auto">
          <DatePicker
            options={{
              defaultDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            }}
          />
        </Col>
      </Row>
      
      {/* Estadísticas */}
      <Row className="mb-3 gy-3">
        {securityStats.map((stat, index) => (
          <Col xs={12} sm={6} md={3} key={index}>
            <SecurityStat {...stat} />
          </Col>
        ))}
      </Row>
      
      {/* Panel principal */}
      <Row className="mb-3 gy-3">
        <Col xs={12} md={4} xl={3}>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as 'tools' | 'systems' | 'scan')}
            className="mb-3"
          >
            <Tab eventKey="tools" title="Herramientas">
              <SecurityTools 
                tools={securityTools} 
                onSelect={setActiveTool} 
                activeToolId={activeTool}
              />
            </Tab>
            <Tab eventKey="systems" title="Sistemas">
              <MonitoredSystems 
                systems={system} 
                onSelect={setActiveSystem} 
                activeSystemId={activeSystem}
              />
            </Tab>
          </Tabs>
        </Col>
        <Col xs={12} md={8} xl={6}>
          <MainPanel />
        </Col>
        <Col xs={12} xl={3}>
          <SecurityAlerts alerts={alerts} />
        </Col>
      </Row>
      
      {/* Estado del sistema */}
      <div className="bg-emphasis p-3 rounded mb-3 border border-secondary">
  <Row className="align-items-center">
    <Col>
      <h6 className="mb-0 d-flex align-items-center text-body-highlight">
        Estado del Sistema 
        <Badge bg="success" className="ms-2">OPERATIVO</Badge>
      </h6>
    </Col>
    <Col xs="auto">
      <div className="d-flex gap-3">
        <span className="d-flex align-items-center">
          <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
          <span className="text-body-highlight">Firewall: Activo</span>
        </span>
        <span className="d-flex align-items-center">
          <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
          <span className="text-body-highlight">VPN: Segura</span>
        </span>
        <span className="d-flex align-items-center">
          <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
          <span className="text-body-highlight">IDS/IPS: Vigilando</span>
        </span>
        <span className="d-flex align-items-center">
          <span className={`badge rounded-circle ${stats.criticalAlerts > 0 ? 'bg-danger' : 'bg-success'} me-1`} style={{ width: 8, height: 8 }}></span>
          <span className="text-body-highlight">Alertas críticas: {stats.criticalAlerts}</span>
        </span>
      </div>
    </Col>
  </Row>
</div>
    </>
  );
};

// Security Stat Component
const SecurityStat: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  variant: string;
  trend?: number;
}> = ({ title, value, icon, variant, trend }) => (
  <Card className="h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center">
        <div 
          className={`me-3 d-flex align-items-center justify-content-center rounded-circle bg-${variant} bg-opacity-10`} 
          style={{ width: 48, height: 48 }}
        >
          {icon}
        </div>
        <div>
          <h6 className="text-body-tertiary fw-semibold mb-0">{title}</h6>
          <h5 className="fw-bold mb-0">{value}</h5>
          {trend !== undefined && (
            <span className={`fs-sm ${trend >= 0 ? 'text-success' : 'text-danger'}`}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs ayer
            </span>
          )}
        </div>
      </div>
    </Card.Body>
  </Card>
);

export default SecurityDashboard;